import React from 'react';
import HBText from '../HBText/HBText';
import { Close12, Close16 } from '@honeybook/icons';
import type { HBTagProps } from './HBTag.types';
import {
    colorMap,
    STRUCTURE,
    TagContainer,
    TagContent,
    DeleteButton,
    PrefixWrapper
} from './HBTag.styles';
import { useTestId } from '../../hooks/useTestId';
import { useLinkBehavior } from '../../hooks/useLinkBehavior/useLinkBehavior';

export type { HBTagColor, HBTagSize, HBTagProps } from './HBTag.types';

const HBTag: React.FC<HBTagProps> = ({
    label,
    color = 'neutral',
    size = 'sm',
    prefix,
    disabled = false,
    onClick,
    onDelete,
    clickableProps,
    deletableProps,
    href,
    target,
    rel,
    download,
    to,
    'data-testid': dataTestId = 'hb-member-tag'
}) => {
    const getTestId = useTestId(dataTestId);
    // The label needs its own testId so HBTagDriver can read the label text without
    // picking up `prefix`, which renders as a sibling inside the same label area.
    const { 'data-testid': labelTestId } = getTestId('label');
    // Destination props are classified by the hook, which owns click rules, `rel` defaults,
    // the disabled link and the missing-`LinkComponent` warning. A `clickableProps.onClick`
    // still works on a link when no top-level `onClick` is set.
    const link = useLinkBehavior({
        to,
        href,
        target,
        rel,
        download,
        onClick: onClick ?? clickableProps?.onClick,
        disabled,
        componentName: 'HBTag'
    });

    const tokens = colorMap[color];
    const structure = STRUCTURE[size];
    const useDisabledOpacity = disabled && !tokens.disabledBorder;
    const borderColor =
        disabled && tokens.disabledBorder
            ? tokens.disabledBorder
            : tokens.border;
    const contentColor =
        disabled && tokens.disabledLabel ? tokens.disabledLabel : tokens.label;
    // A disabled link stays an anchor so it keeps a tab stop, but it is not interactive: no
    // hover, press or pointer, only a keyboard focus ring.
    const isDisabledLink = link.isLink && disabled;
    const isClickable = !link.isLink && !!onClick && !disabled;
    const isInteractive = (link.isLink && !disabled) || isClickable;

    const resolvedTokens = {
        background: tokens.background,
        border: borderColor,
        color: contentColor,
        hover: tokens.hover,
        pressed: tokens.pressed
    };

    const renderPrefix = () => {
        if (!prefix) {
            return null;
        }

        if (React.isValidElement(prefix)) {
            return React.cloneElement(
                prefix as React.ReactElement<{ color?: string }>,
                { color: 'currentColor' }
            );
        }

        return <PrefixWrapper>{prefix}</PrefixWrapper>;
    };

    return (
        <TagContainer
            $border={borderColor}
            $height={structure.height}
            $borderRadius={structure.borderRadius}
            $disabled={disabled}
            $disabledLink={isDisabledLink}
            {...(disabled && { 'data-disabled': 'true' })}
            {...getTestId()}
        >
            <TagContent
                as={link.isLink ? link.as : isClickable ? 'button' : 'span'}
                {...(isClickable && { type: 'button' })}
                {...(isInteractive && { ...clickableProps })}
                {...(link.isLink && link.linkProps)}
                {...(isClickable && onClick && { onClick })}
                $tokens={resolvedTokens}
                $structure={{
                    padding: structure.padding,
                    gap: structure.gap,
                    innerBorderRadius: structure.innerBorderRadius
                }}
                $interactive={isInteractive}
                $focusableDisabled={isDisabledLink}
                $disabledOpacity={useDisabledOpacity}
                {...getTestId('content')}
            >
                {renderPrefix()}
                <HBText
                    type={structure.textType}
                    color="inherit"
                    testId={labelTestId}
                >
                    {label}
                </HBText>
            </TagContent>
            {onDelete && (
                <DeleteButton
                    type="button"
                    aria-label={`Remove ${label}`}
                    {...deletableProps}
                    onClick={onDelete}
                    $tokens={resolvedTokens}
                    $innerBorderRadius={structure.innerBorderRadius}
                    $disabled={disabled}
                    $disabledOpacity={useDisabledOpacity}
                    disabled={disabled}
                    {...getTestId('delete')}
                >
                    {size === 'sm' ? (
                        <Close12 color="currentColor" />
                    ) : (
                        <Close16 color="currentColor" />
                    )}
                </DeleteButton>
            )}
        </TagContainer>
    );
};

export default HBTag;
